// src/App.jsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from './context/AuthContext';
import { OptionsProvider } from './context/OptionsContext';
import { SettingsProvider } from './context/SettingsContext';
import { useSettings } from './context/SettingsContext';
import { ThemeProvider as CustomThemeProvider } from './context/ThemeContext';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import { Box, CircularProgress } from '@mui/material';

// ============================================================
// AUTH PAGES
// ============================================================
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// ============================================================
// VENDOR PAGES
// ============================================================
import VendorDashboard from './pages/vendor/Dashboard';
import VendorProducts from './pages/vendor/Products';
import VendorOrders from './pages/vendor/Orders';
import VendorCustomers from './pages/vendor/Customers';
import VendorAnalytics from './pages/vendor/Analytics';
import VendorChat from './pages/vendor/Chat';
import VendorProfile from './pages/vendor/Profile';
import VendorSettings from './pages/vendor/Settings';
import VendorPayouts from './pages/vendor/Payouts';
import VendorKYC from './pages/vendor/KYC';
import VendorCoupons from './pages/vendor/Coupons';
import VendorSupport from './pages/vendor/Support';
// ✅ NEW: Vendor Transactions
import VendorTransactions from './pages/vendor/Transactions';

// ============================================================
// ADMIN PAGES
// ============================================================
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminVendors from './pages/admin/Vendors';
import AdminOrders from './pages/admin/Orders';
import AdminProducts from './pages/admin/Products';
import AdminCategories from './pages/admin/Categories';
import AdminSizes from './pages/admin/Sizes';
import AdminColors from './pages/admin/Colors';
import AdminBadges from './pages/admin/Badges';
import AdminProfile from './pages/admin/Profile';
import AdminSettings from './pages/admin/Settings';
import AdminPayouts from './pages/admin/Payouts';
import AdminKYC from './pages/admin/KYC';
import AdminCoupons from './pages/admin/Coupons';
import AdminMessages from './pages/admin/Messages';
import AdminThemeSettings from './pages/admin/ThemeSettings';
import AdminSupport from './pages/admin/Support';
import AdminLogistics from './pages/admin/Logistics';
// ✅ NEW: Admin Transactions
import AdminTransactions from './pages/admin/Transactions';

// ============================================================
// SHARED PAGES
// ============================================================
import NotFound from './pages/NotFound';

// ============================================================
// DYNAMIC HELMET COMPONENT
// ============================================================
const DynamicHelmet = () => {
  const { siteName, siteDescription } = useSettings();
  return (
    <Helmet>
      <title>{siteName || 'SCITECH E-Commerce Portal'}</title>
      <meta name="description" content={siteDescription || 'Vendor and Admin Portal for SCITECH E-Commerce'} />
    </Helmet>
  );
};

// ============================================================
// ROOT REDIRECT
// ============================================================
const RootRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress color="error" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return user?.role === 'admin' ? (
    <Navigate to="/admin/dashboard" replace />
  ) : (
    <Navigate to="/vendor/dashboard" replace />
  );
};

// ============================================================
// MAIN APP COMPONENT
// ============================================================
function App() {
  return (
    <CustomThemeProvider>
      <SettingsProvider>
        <DynamicHelmet />
        <OptionsProvider>
          <Routes>
            {/* ============================================================
                AUTH ROUTES (Public)
            ============================================================ */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* ============================================================
                PROTECTED ROUTES (With Layout)
            ============================================================ */}
            <Route element={<Layout />}>
              
              {/* ============================================================
                  VENDOR ROUTES
              ============================================================ */}
              <Route element={<ProtectedRoute allowedRoles={['vendor']} />}>
                <Route path="/vendor/dashboard" element={<VendorDashboard />} />
                <Route path="/vendor/products" element={<VendorProducts />} />
                <Route path="/vendor/orders" element={<VendorOrders />} />
                <Route path="/vendor/customers" element={<VendorCustomers />} />
                <Route path="/vendor/analytics" element={<VendorAnalytics />} />
                <Route path="/vendor/chat" element={<VendorChat />} />
                <Route path="/vendor/profile" element={<VendorProfile />} />
                <Route path="/vendor/settings" element={<VendorSettings />} />
                <Route path="/vendor/payouts" element={<VendorPayouts />} />
                <Route path="/vendor/kyc" element={<VendorKYC />} />
                <Route path="/vendor/coupons" element={<VendorCoupons />} />
                <Route path="/vendor/support" element={<VendorSupport />} />
                <Route path="/vendor/support/tickets/:id" element={<VendorSupport />} />
                {/* ✅ NEW: Vendor Transactions */}
                <Route path="/vendor/transactions" element={<VendorTransactions />} />
              </Route>

              {/* ============================================================
                  ADMIN ROUTES
              ============================================================ */}
              <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/vendors" element={<AdminVendors />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/sizes" element={<AdminSizes />} />
                <Route path="/admin/colors" element={<AdminColors />} />
                <Route path="/admin/badges" element={<AdminBadges />} />
                <Route path="/admin/profile" element={<AdminProfile />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/payouts" element={<AdminPayouts />} />
                <Route path="/admin/kyc" element={<AdminKYC />} />
                <Route path="/admin/coupons" element={<AdminCoupons />} />
                <Route path="/admin/messages" element={<AdminMessages />} />
                <Route path="/admin/theme-settings" element={<AdminThemeSettings />} />
                <Route path="/admin/support" element={<AdminSupport />} />
                <Route path="/admin/support/tickets/:id" element={<AdminSupport />} />
                <Route path="/admin/logistics" element={<AdminLogistics />} />
                {/* ✅ NEW: Admin Transactions */}
                <Route path="/admin/transactions" element={<AdminTransactions />} />
              </Route>

            </Route>

            {/* ============================================================
                ROOT & 404
            ============================================================ */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<NotFound />} />
            
          </Routes>
        </OptionsProvider>
      </SettingsProvider>
    </CustomThemeProvider>
  );
}

export default App;